import React from 'react';
import { useDarkClasses } from '../../../../hooks/useDarkClasses';
import { FANTASY_MODULE_DATA, FANTASY_SUGGESTIONS } from '../genreModuleData';
import { SearchableChipField, SingleSelectField } from './shared';
const ACCENT = 'bg-purple-100 text-purple-700 border-purple-500 dark:bg-purple-900/30 dark:text-purple-300';
// ═══════════════════════════════════════════════════
// FULL FANTASY MODULE
// ═══════════════════════════════════════════════════
interface FantasyModuleProps {
  subtype: string;
  setSubtype: (v: string) => void;
  magicSystem: string;
  setMagicSystem: (v: string) => void;
  worldStructure: string;
  setWorldStructure: (v: string) => void;
  storyEngines: string[];
  setStoryEngines: React.Dispatch<React.SetStateAction<string[]>>;
  creatureTags: string[];
  setCreatureTags: React.Dispatch<React.SetStateAction<string[]>>;
  tone: string;
  setTone: (v: string) => void;
}
export const FantasyModule: React.FC<FantasyModuleProps> = ({
  subtype,
  setSubtype,
  magicSystem,
  setMagicSystem,
  worldStructure,
  setWorldStructure,
  storyEngines,
  setStoryEngines,
  creatureTags,
  setCreatureTags,
  tone,
  setTone
}) => {
  const dk = useDarkClasses();
  const toggleEngine = (e: string) => setStoryEngines(prev => prev.includes(e) ? prev.filter(x => x !== e) : [...prev, e]);
  const toggleCreature = (c: string) => setCreatureTags(prev => prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]);
  return <div className="space-y-8" data-id="element-20551" data-mp="2xcn07hl2en9u">
      {/* Fantasy Subtype */}
      <SingleSelectField label="Fantasy Subtype" placeholder="e.g. High Fantasy, Urban Fantasy, Dark Fantasy..." items={FANTASY_MODULE_DATA.subtypes} value={subtype} onChange={setSubtype} accentClass={ACCENT} data-id="element-20552" data-mp="2xcwq75640ufj" />

      {/* Magic System Type */}
      <SingleSelectField label="Magic System Type" placeholder="e.g. Hard Magic, Soft Magic, Elemental..." items={FANTASY_MODULE_DATA.magicSystems} value={magicSystem} onChange={setMagicSystem} accentClass={ACCENT} data-id="element-20553" data-mp="29msmkxsx8uoj" />

      {/* World Structure */}
      <SingleSelectField label="World Structure" placeholder="e.g. Single World, Multiverse, Portal..." items={FANTASY_MODULE_DATA.worldStructures} value={worldStructure} onChange={setWorldStructure} accentClass={ACCENT} data-id="element-20554" data-mp="21m34cts5ycva" />

      {/* Story Engines */}
      <SearchableChipField label="Fantasy Story Engines" placeholder="e.g. Chosen One, Quest, Rebellion..." items={FANTASY_MODULE_DATA.storyEngines} selected={storyEngines} onToggle={toggleEngine} accentClass={ACCENT} data-id="element-20555" data-mp="1nooa9bqytgwz" />

      {/* Creature / Being Tags */}
      <SearchableChipField label="Creature / Being Tags" placeholder="e.g. Dragons, Fae, Vampires..." items={FANTASY_MODULE_DATA.creatureTags} selected={creatureTags} onToggle={toggleCreature} accentClass={ACCENT} data-id="element-20556" data-mp="1so196uv42efx" />

      {/* Fantasy Tone */}
      <div data-id="element-20557" data-mp="3bmpdlmc9muxg">
        <label className={`block text-sm font-medium mb-2 ${dk.textSecondary}`} data-id="element-20558" data-mp="3gjfu2arzfsje">
          Fantasy Tone
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-id="element-20559" data-mp="32k6ad14kigao">
          {FANTASY_MODULE_DATA.tones.map(t => {
          const isSelected = tone === t.level;
          return <button key={t.level} onClick={() => setTone(isSelected ? '' : t.level)} className={`p-3 rounded-xl border text-left transition-all
                  ${isSelected ? 'bg-purple-700 text-white border-purple-700 dark:bg-purple-300 dark:text-purple-900' : `${dk.surfaceMuted} ${dk.border} hover:border-purple-400`}`} data-id="element-20560" data-mp="3uakkqnddhahl">
                <div className={`font-bold text-sm mb-1 ${isSelected ? '' : dk.textPrimary}`} data-id="element-20561" data-mp="h3smr5mbguvp">
                  {t.level}
                </div>
                <div className={`text-xs ${isSelected ? 'opacity-90' : dk.textMuted}`} data-id="element-20562" data-mp="2uc3n74g7r6nh">
                  {t.description}
                </div>
              </button>;
        })}
        </div>
      </div>
    </div>;
};
// ═══════════════════════════════════════════════════
// COMPACT FANTASY CROSSOVER
// ═══════════════════════════════════════════════════
interface FantasyModuleCompactProps {
  subtype: string;
  setSubtype: (v: string) => void;
  magicSystem: string;
  setMagicSystem: (v: string) => void;
  creatureTags: string[];
  setCreatureTags: React.Dispatch<React.SetStateAction<string[]>>;
  worldStructure: string;
  setWorldStructure: (v: string) => void;
}
export const FantasyModuleCompact: React.FC<FantasyModuleCompactProps> = ({
  subtype,
  setSubtype,
  magicSystem,
  setMagicSystem,
  creatureTags,
  setCreatureTags,
  worldStructure,
  setWorldStructure
}) => {
  const toggleCreature = (c: string) => setCreatureTags(prev => prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]);
  return <div className="space-y-5" data-id="element-20563" data-mp="1dlsxg4aky78z">
      <SingleSelectField label="Fantasy Subtype" placeholder="Search subtypes..." items={FANTASY_MODULE_DATA.subtypes} value={subtype} onChange={setSubtype} accentClass={ACCENT} data-id="element-20564" data-mp="1iib08ymcnvio" />

      <SingleSelectField label="Magic System Type" placeholder="Search magic systems..." items={FANTASY_MODULE_DATA.magicSystems} value={magicSystem} onChange={setMagicSystem} accentClass={ACCENT} data-id="element-20565" data-mp="1rkan47k1pttj" />

      <SearchableChipField label="Creature / Being Tags" placeholder="Search creatures..." items={FANTASY_MODULE_DATA.creatureTags} selected={creatureTags} onToggle={toggleCreature} accentClass={ACCENT} data-id="element-20566" data-mp="268njv6qyjgp3" />

      <SingleSelectField label="World Structure" placeholder="Search world structures..." items={FANTASY_MODULE_DATA.worldStructures} value={worldStructure} onChange={setWorldStructure} accentClass={ACCENT} data-id="element-20567" data-mp="2fawq3kf70nwo" />
    </div>;
};